// src/_components/SavedComponent.js
import React, { useState } from 'react';
import {
    Button,
    InputGroup,
    FormControl,
    Card,
    Row,
    Col,
    Badge
} from 'react-bootstrap';
import PlaceDetailModal from './PlaceDetailModal';

const SavedComponent = ({
    favorites = [],
    allRecommendations = [],
    onRemoveFromFavorites,
    onAddToMyTrip,
    onLocationSelect
}) => {
    const [activeTab, setActiveTab] = useState('For you');
    const [searchTerm, setSearchTerm] = useState('');
    const [showDetailModal, setShowDetailModal] = useState(false);
    const [selectedPlaceForModal, setSelectedPlaceForModal] = useState(null);

    // 统一的菜单选项配置 - 与ExploreComponent保持完全一致
    const menuTabs = [
        {
            key: 'For you',
            label: 'For you',
            icon: 'bi-stars',
            color: 'dark'
        },
        {
            key: 'Stays',
            label: 'Stays',
            icon: 'bi-house-door',
            color: 'dark'
        },
        {
            key: 'Restaurants',
            label: 'Restaurants',
            icon: 'bi-cup-hot',
            color: 'dark'
        },
        {
            key: 'Things to do',
            label: 'Things to do',
            icon: 'bi-star',
            color: 'dark'
        }
    ];

    // 获取收藏的地点详细信息
    const getFavoriteItems = () => {
        return allRecommendations.filter(item => favorites.includes(item.id));
    };

    // 按类型分组收藏
    const groupFavoritesByType = () => {
        const favoriteItems = getFavoriteItems();
        const grouped = {
            restaurants: [],
            activities: [],
            stays: [],
            locations: []
        };

        favoriteItems.forEach(item => {
            if (item.cuisine) {
                grouped.restaurants.push(item);
            } else if (item.budgetLevel || item.fullData?.place_id === 'hotel_checkin') {
                grouped.stays.push(item);
            } else if (item.description || item.fullData?.activity_desc) {
                grouped.activities.push(item);
            } else {
                grouped.locations.push(item);
            }
        });

        return grouped;
    };

    // 处理地点点击 - 显示详情
    const handleLocationClick = (location) => {
        console.log('🎯 Saved location clicked:', location);

        setSelectedPlaceForModal({
            data: location.fullData || location,
            name: location.name
        });
        setShowDetailModal(true);

        if (onLocationSelect) {
            onLocationSelect(location);
        }
    };

    // 处理取消收藏
    const handleRemoveFromFavorites = (location) => {
        console.log('💔 Removing from favorites:', location);
        if (onRemoveFromFavorites) {
            onRemoveFromFavorites(location);
        }
    };

    // 处理添加到我的行程
    const handleAddToMyTrip = (location) => {
        console.log('➕ Adding to my trip from saved:', location);
        if (onAddToMyTrip) {
            onAddToMyTrip(location);
        }
        setShowDetailModal(false);
    };

    const handleCloseModal = () => {
        setShowDetailModal(false);
        setSelectedPlaceForModal(null);
    };

    const renderStarRating = (rating) => {
        const numRating = parseFloat(rating || 0);
        const stars = [];
        const fullStars = Math.floor(numRating);
        const hasHalfStar = numRating % 1 !== 0;

        for (let i = 0; i < fullStars; i++) {
            stars.push(<span key={i} style={{ color: '#ffc107' }}>★</span>);
        }

        if (hasHalfStar) {
            stars.push(<span key="half" style={{ color: '#ffc107' }}>☆</span>);
        }

        const emptyStars = 5 - Math.ceil(numRating);
        for (let i = 0; i < emptyStars; i++) {
            stars.push(<span key={`empty-${i}`} style={{ color: '#dee2e6' }}>☆</span>);
        }

        return stars;
    };

    // 与ExploreComponent样式完全一致的卡片组件
    const renderFavoriteCard = (item, type = 'location') => (
        <Card
            key={item.id}
            className="border-0 shadow-sm"
            style={{
                cursor: 'pointer',
                transition: 'all 0.2s ease',
                height: '320px' // 与ExploreComponent保持一致的高度
            }}
            onClick={() => handleLocationClick(item)}
        >
            <div style={{ position: 'relative', height: '150px', overflow: 'hidden' }}>
                <Card.Img
                    variant="top"
                    src={item.image}
                    style={{
                        height: '100%',
                        objectFit: 'cover',
                        transition: 'transform 0.3s ease'
                    }}
                    onError={(e) => {
                        e.target.src = 'https://images.unsplash.com/photo-1539650116574-75c0c6d73c3e?w=800';
                    }}
                />

                {/* 收藏标识 - 右上角红色心形 */}
                <Button
                    variant="link"
                    className="position-absolute top-0 end-0 m-2"
                    style={{
                        fontSize: '1.2rem',
                        color: '#e74c3c',
                        textShadow: '0 0 3px rgba(0,0,0,0.5)'
                    }}
                    onClick={(e) => {
                        e.stopPropagation();
                        handleRemoveFromFavorites(item);
                    }}
                >
                    <i className="bi bi-heart-fill"></i>
                </Button>

                {/* 预算等级标签 - 仅酒店显示 */}
                {type === 'stay' && item.budgetLevel && (
                    <div className={`position-absolute top-0 start-0 m-2 px-2 py-1 rounded small text-white ${item.budgetLevel === 'Basic' ? 'bg-success' :
                        item.budgetLevel === 'Signature' ? 'bg-warning' : 'bg-danger'
                        }`}>
                        {item.budgetLevel}
                    </div>
                )}

                {item.cuisine && (
                    <div className="position-absolute bottom-0 start-0 m-2 bg-dark text-white px-2 py-1 rounded small">
                        {item.cuisine}
                    </div>
                )}
            </div>

            <Card.Body className="p-3 d-flex flex-column" style={{ height: '200px' }}>
                {/* 头部信息 - 与ExploreComponent一致 */}
                <div className="d-flex justify-content-between align-items-start mb-2" style={{ minHeight: '45px' }}>
                    <h6 className="mb-0 fw-bold flex-grow-1" style={{
                        fontSize: '0.95rem',
                        lineHeight: '1.3',
                        overflow: 'hidden',
                        display: '-webkit-box',
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: 'vertical'
                    }}>
                        {item.name}
                    </h6>
                    {type === 'stay' ? (
                        <div className="text-end ms-2" style={{ minWidth: '70px' }}>
                            <div className="fw-bold" style={{ fontSize: '0.85rem' }}>
                                {item.priceRange || item.price}
                            </div>
                            <small className="text-muted">{item.priceUnit}</small>
                        </div>
                    ) : (
                        <div className="d-flex align-items-center ms-2" style={{ fontSize: '0.8rem' }}>
                            {renderStarRating(item.rating)}
                            <small className="text-muted ms-1">({item.reviews})</small>
                        </div>
                    )}
                </div>

                {/* 位置和价格信息 */}
                <div className="d-flex align-items-center justify-content-between mb-2">
                    <div className="d-flex align-items-center text-muted">
                        <i className="bi bi-geo-alt me-1"></i>
                        <small style={{ fontSize: '0.8rem' }}>{item.location}</small>
                    </div>

                    {item.cost && (
                        <small className="text-success fw-bold">
                            ${item.cost}
                        </small>
                    )}
                </div>

                {/* 描述 - 固定行数 */}
                {item.description && (
                    <div className="mb-2" style={{ flex: '1 1 auto' }}>
                        <small className="text-muted" style={{
                            fontSize: '0.8rem',
                            lineHeight: '1.3',
                            overflow: 'hidden',
                            display: '-webkit-box',
                            WebkitLineClamp: 2,
                            WebkitBoxOrient: 'vertical'
                        }}>
                            {item.description}
                        </small>
                    </div>
                )}

                {/* 酒店设施 - 底部固定位置 */}
                {type === 'stay' && item.amenities && (
                    <div className="mt-auto">
                        <div className="d-flex flex-wrap gap-1">
                            {item.amenities.slice(0, 2).map((amenity, index) => (
                                <span key={index} className="badge bg-light text-dark" style={{ fontSize: '0.7rem' }}>
                                    {amenity}
                                </span>
                            ))}
                            {item.amenities.length > 2 && (
                                <span className="badge bg-light text-dark" style={{ fontSize: '0.7rem' }}>
                                    +{item.amenities.length - 2}
                                </span>
                            )}
                        </div>
                    </div>
                )}

                {/* Add to Trip 按钮 - 固定在底部 */}
                {!(type === 'stay' && item.amenities) && (
                    <div className="mt-2">
                        <Button
                            variant="outline-success"
                            size="sm"
                            className="w-100"
                            style={{ fontSize: '0.75rem', padding: '0.25rem 0.5rem' }}
                            onClick={(e) => {
                                e.stopPropagation();
                                handleAddToMyTrip(item);
                            }}
                        >
                            <i className="bi bi-plus-circle me-1"></i>
                            Add to MyTrip
                        </Button>
                    </div>
                )}
            </Card.Body>
        </Card>
    );

    // 占位卡片 - 与ExploreComponent高度一致
    const renderPlaceholderCard = (index) => (
        <Card
            key={`placeholder-${index}`}
            className="border-0 shadow-sm"
            style={{
                backgroundColor: '#f8f9fa',
                height: '320px' // 与ExploreComponent保持一致
            }}
        >
            <div style={{
                position: 'relative',
                height: '150px',
                backgroundColor: '#e9ecef',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
            }}>
                <i className="bi bi-heart" style={{
                    fontSize: '2.5rem',
                    color: '#adb5bd'
                }}></i>

                <div style={{
                    position: 'absolute',
                    top: '10px',
                    left: '10px',
                    right: '10px',
                    bottom: '10px',
                    border: '2px dashed #ced4da',
                    borderRadius: '8px'
                }}></div>
            </div>

            <Card.Body className="p-3 d-flex flex-column" style={{ height: '170px' }}>
                <div className="text-center flex-grow-1 d-flex flex-column justify-content-center">
                    <h6 className="mb-2 text-muted">Saved place will appear here</h6>
                    <div className="d-flex align-items-center justify-content-center text-muted mb-2">
                        <i className="bi bi-geo-alt me-1"></i>
                        <small>Location</small>
                    </div>
                </div>
            </Card.Body>
        </Card>
    );

    const favoriteItems = getFavoriteItems();
    const groupedFavorites = groupFavoritesByType();

    const getCurrentTabData = () => {
        switch (activeTab) {
            case 'Restaurants':
                return groupedFavorites.restaurants;
            case 'Things to do':
                return groupedFavorites.activities;
            case 'Stays':
                return groupedFavorites.stays;
            default:
                return favoriteItems; // For you 显示所有收藏
        }
    };

    // 获取当前tab的计数
    const getCurrentTabCount = () => {
        const data = getCurrentTabData();
        return data.length;
    };

    // 生成卡片数组 - 包含真实数据和占位卡片
    const generateCardArray = () => {
        const currentData = getCurrentTabData().filter(item =>
            searchTerm === '' ||
            item.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
            item.location.toLowerCase().includes(searchTerm.toLowerCase())
        );

        const minCards = 9; // 最少显示9个卡片位置（3x3网格）
        const cards = [];

        // 添加真实数据卡片
        currentData.forEach((item, index) => {
            cards.push({
                type: 'data',
                content: renderFavoriteCard(
                    item,
                    activeTab === 'Stays' ? 'stay' : 'location'
                ),
                key: item.id
            });
        });

        // 添加占位卡片填充到minCards数量
        for (let i = currentData.length; i < minCards; i++) {
            cards.push({
                type: 'placeholder',
                content: renderPlaceholderCard(i),
                key: `placeholder-${i}`
            });
        }

        return cards;
    };

    return (
        <div className="h-100 d-flex flex-column">
            {/* Header */}
            <div className="border-bottom bg-white">
                <div className="d-flex align-items-center justify-content-between p-3">
                    <h4 className="mb-0">❤️ Saved Places</h4>
                    <Badge bg="danger" className="fs-6">
                        {favoriteItems.length} favorites
                    </Badge>
                </div>

                {/* Search Bar */}
                <div className="px-3 pb-3">
                    <InputGroup>
                        <InputGroup.Text>
                            <i className="bi bi-search"></i>
                        </InputGroup.Text>
                        <FormControl
                            placeholder="Search your saved places..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                        />
                    </InputGroup>
                </div>

                {/* Navigation Tabs */}
                <div className="d-flex border-bottom px-3" style={{ backgroundColor: '#f8f9fa' }}>
                    {menuTabs.map(tab => (
                        <Button
                            key={tab.key}
                            variant="link"
                            className={`text-decoration-none px-3 py-2 border-0 rounded-0 d-flex align-items-center gap-2 ${activeTab === tab.key
                                ? 'text-dark border-bottom border-dark border-3 bg-white'
                                : 'text-muted bg-transparent'
                                }`}
                            style={{
                                fontWeight: activeTab === tab.key ? '600' : '400',
                                transition: 'all 0.2s ease'
                            }}
                            onClick={() => setActiveTab(tab.key)}
                        >
                            <i className={`${tab.icon}`} style={{ fontSize: '0.9rem' }}></i>
                            <span style={{ fontSize: '0.9rem' }}>
                                {tab.label}
                                {/* 显示计数 */}
                                {tab.key === activeTab && (
                                    <span className="ms-1 text-muted">({getCurrentTabCount()})</span>
                                )}
                            </span>
                        </Button>
                    ))}
                </div>

                {/* 状态提示 */}
                <div className="px-3 py-2 bg-light d-flex justify-content-between align-items-center">
                    <div>
                        <small className="text-muted">
                            Showing {getCurrentTabCount()} saved places
                            {activeTab !== 'For you' && ` in ${activeTab}`}
                        </small>
                    </div>
                </div>
            </div>

            {/* Content */}
            <div className="flex-grow-1 overflow-auto p-3">
                <Row className="g-3">
                    {generateCardArray().map(card => (
                        <Col key={card.key} sm={6} lg={4}>
                            {card.content}
                        </Col>
                    ))}
                </Row>

                {/* 搜索无结果提示 */}
                {getCurrentTabData().filter(item =>
                    searchTerm === '' ||
                    item.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                    item.location.toLowerCase().includes(searchTerm.toLowerCase())
                ).length === 0 && favoriteItems.length > 0 && searchTerm !== '' && (
                        <div className="text-center py-3">
                            <p className="text-muted">No places found matching "{searchTerm}"</p>
                        </div>
                    )}
            </div>

            {/* 详情弹窗 */}
            <PlaceDetailModal
                show={showDetailModal}
                onHide={handleCloseModal}
                placeData={selectedPlaceForModal?.data}
                placeName={selectedPlaceForModal?.name}
                additionalActions={
                    <Button
                        variant="success"
                        className="me-2"
                        onClick={() => handleAddToMyTrip(selectedPlaceForModal?.data)}
                    >
                        <i className="bi bi-plus-circle me-1"></i>
                        Add to My Trip
                    </Button>
                }
            />
        </div>
    );
};

export default SavedComponent;